import classNames from 'classnames';
import moment from 'jalali-moment';
import { Alert, App } from 'antd';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'motion/react';
import { useSelfContext } from '@/context';
import { Flex, LoadingSpin } from '@/components';
import { useModal } from '@/hooks';
import { useLocaleStore } from '@/store';
import ReserveCard from './ReserveCard';

export interface ReserveProgramDesktopProps {
  isNewReserveMode?: boolean;
  variant: AppModules;
  programs: SelfMealEntity[];
  groupedPrograms: Dictionary<SelfMealEntity[]>;
  isProgramsFetching: boolean;
  detailModal: ReturnType<typeof useModal>;
  changeSelfModal: ReturnType<typeof useModal>;
  nextWeek: () => void;
  prevWeek: () => void;
  locked: boolean;
  setLocked: (value: boolean) => any;
}

export default function ReserveProgramDesktop({
  isNewReserveMode = false,
  variant,
  programs,
  groupedPrograms,
  isProgramsFetching,
  detailModal,
  changeSelfModal,
  nextWeek,
  prevWeek,
  locked,
  setLocked
}: ReserveProgramDesktopProps) {

  const isRTL = useLocaleStore(state => state.dir) === 'rtl';
  const { selfText } = useSelfContext();
  const [t] = useTranslation();
  const { message: messageAPI } = App.useApp();

  return (
    <>
      <Flex className="reserve-programs-container reserve-programs-container-desktop" direction="column">
        <Flex className={`week-change-nav week-change-nav-${variant}`}>
          <Flex className="week-change-nav-btn" onClick={() => !isProgramsFetching && prevWeek()}>
            <span className="arrow-circle">
              <span
                className={
                  classNames('icon-xs text-primary-food',
                    isRTL ? 'flaticon-right-chevron' : 'flaticon-left-chevron'
                  )}
              />
            </span>
            <span>{t('common:labels.prev-week')}</span>
          </Flex>
          <Flex className="week-change-nav-btn !ms-auto" onClick={() => {
            !isProgramsFetching && nextWeek();
          }}>
            <span>{t('common:labels.next-week')}</span>
            <span className="arrow-circle">
              <span
                className={
                  classNames('icon-xs text-primary-food',
                    isRTL ? 'flaticon-left-chevron' : 'flaticon-right-chevron'
                  )}
              />
            </span>
          </Flex>
        </Flex>
        <Flex className="reserve-programs-content min-h-[200px]" direction="column">
          {!isProgramsFetching && programs.length === 0 && (
            <Flex direction="column" className="w-full text-center">
              {Object.entries(groupedPrograms).map(([date, _program]) => (
                <Flex inlineFlex key={date} className="reserve-program-col" direction="column">
                  <Flex className="week-day-name">
                    <span>
                      {moment.from(date, 'en', 'YYYY-MM-DD').format('ddd، jDD jMMM jYYYY')}
                    </span>
                  </Flex>
                </Flex>
              ))}
              <Alert type="warning" className="flex mx-auto max-h-16 mt-2" message={selfText('empty-items')} />
            </Flex>
          )}
          <AnimatePresence mode="wait">
            {isProgramsFetching ?
              <LoadingSpin className="mt-10" /> :
              <Flex direction="column" className="w-full">
                <motion.div
                  key="program-content"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="sticky top-0 z-10 flex">
                  {Object.entries(groupedPrograms).map(([date, _program]) => {
                    return (
                      <Flex gap={0} inlineFlex key={date} className="reserve-program-col" direction="column">
                        <Flex className="week-day-name">
                          <span>
                            {moment.from(date, 'en', 'YYYY-MM-DD').format('ddd، jDD jMMM jYYYY')}
                          </span>
                        </Flex>
                      </Flex>
                    );
                  })}
                </motion.div>
                <motion.div
                  className="flex"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                >
                  {Object.entries(groupedPrograms).map(([date, programs], i) => {
                    return (
                      <Flex inlineFlex key={date} className="reserve-program-col" direction="column">
                        {programs.map(meal => (
                          meal.programFoodTypes?.map(reserve => (
                            reserve?.foodList?.map(subFood => (
                              <ReserveCard
                                isNewReserveMode={isNewReserveMode}
                                locked={locked}
                                setLocked={setLocked}
                                key={`${i} ${meal.mealTypeId} ${reserve.id} ${reserve.foodNames} ${subFood.id}`}
                                meal={meal}
                                reserve={reserve}
                                messageAPI={messageAPI}
                                onDetailClick={() => detailModal.show({ reserve, meal })}
                                onChangeSelf={() => changeSelfModal.show({ reserve, meal })}
                              />
                            ))
                          ))
                        ))}
                      </Flex>
                    );
                  })}
                </motion.div>
              </Flex>
            }
          </AnimatePresence>
        </Flex>
      </Flex>
    </>
  );
}
